import { useChatStore } from './chatStore';
import { peekGatewaySocket } from './gatewaySocket';
import type { ChatAttachment } from './types';

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function buildDisplayContent(text: string, files: ChatAttachment[]): string {
  if (files.length === 0) return text;
  const names = files.map((f) => f.name).join(' · ');
  return `📎 ${names}\n\n${text}`;
}

export interface SendMessagePayload {
  text: string;
  files?: ChatAttachment[];
}

export function useSendMessage() {
  const {
    addMessage,
    setProcessing,
    setSessionId,
    setTurnId,
    sessionId,
    selectedAgentId,
    selectedModelId,
    useAgent,
    systemPrompt,
    isProcessing,
  } = useChatStore();

  const sendMessage = async ({ text, files = [] }: SendMessagePayload): Promise<void> => {
    const socket = peekGatewaySocket();

    if (!socket?.connected) {
      addMessage({
        id: makeId(),
        role: 'assistant',
        content: 'Not connected to the agent. Please wait and try again.',
        timestamp: new Date().toISOString(),
        status: 'error',
      });
      return;
    }

    if (isProcessing) {
      return;
    }

    const effectiveSessionId = sessionId ?? crypto.randomUUID();

    if (!sessionId) {
      setSessionId(effectiveSessionId);
    }

    addMessage({
      id: makeId(),
      role: 'user',
      content: buildDisplayContent(text, files),
      timestamp: new Date().toISOString(),
      status: 'sent',
    });

    setProcessing(true);

    const attachments = files
      .filter((f) => f.text !== undefined)
      .map((f) => ({
        fileName: f.name,
        content: f.text?.slice(0, 100_000) ?? '',
        mimeType: 'text/plain',
      }));

    socket.emit('message', {
      type: 'message',
      sessionId: effectiveSessionId,
      content: buildApiContent(text, files),
      ...(useAgent && selectedAgentId ? { agentId: selectedAgentId } : {}),
      ...(selectedModelId ? { modelId: selectedModelId } : {}),
      ...(systemPrompt ? { systemPrompt } : {}),
      ...(attachments.length > 0 ? { attachments } : {}),
    });

    socket.once('ack', (data) => {
      setTurnId(data.turnId);
    });
  };

  return {
    sendMessage,
    isPending: isProcessing,
  };
}

function buildApiContent(text: string, files: ChatAttachment[]): string {
  const textFiles = files.filter((f) => f.text !== undefined);
  if (textFiles.length === 0) return text;
  const fileSection = files
    .map((f) =>
      f.text !== undefined
        ? `\n📄 ${f.name}:\n\`\`\`\n${f.text.slice(0, 8000)}\n\`\`\``
        : `\n📎 ${f.name} (${formatBytes(f.size)} — binary)`,
    )
    .join('\n');
  return `${text}\n\n---\nAttached files:${fileSection}`;
}
